import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import {
    Client,
    ClientDuplicityTypes,
    createClient,
    createDuplicateClient,
    CustomField,
    CustomFieldTypes,
} from './client.model';
import { ClientStore } from './client.store';
import {
    AesRequestFieldModel,
    AesRequestModel,
} from '@core/models/AesRequest.model';
import { SessionQuery } from '@state/session/session.query';
import { EMPTY, Observable } from 'rxjs';
import { ClientQuery } from '@state/clients/client.query';
import {
    DEF_NUMBER_OF_CLIENT_CUSTOM_FIELDS,
    FLOW_DATE_FORMAT,
} from '@core/constants';
import { SettingsQuery } from '@state/settings/settings.query';
import { ClinicQuery } from '@state/clinic/clinic.query';
import moment from 'moment';
import { ClientDocument } from '@state/clients/client-documents/client-document.model';
import {
    ClientNextHistory,
    ClientPreviousHistory,
    createClientNextHistory,
    createClientPreviousHistory,
} from '@state/clients/client-history.model';
import { DocumentEditFieldType } from '@core/models/DocumentEditField';
import { ApiService } from '@core/services/api/api.service';
import {
    ClientClinicHist,
    ClientClinicHistClinicAnnotation,
    createClientClinicHist,
} from '@core/models/ClientClinicHist';
import { DoctorMode } from '@core/models/DoctorMode.model';

@Injectable({ providedIn: 'root' })
export class ClientService {
    constructor(
        private apiService: ApiService,
        private clientQuery: ClientQuery,
        private clientStore: ClientStore,
        private clinicQuery: ClinicQuery,
        private http: HttpClient,
        private sessionQuery: SessionQuery,
        private settingsQuery: SettingsQuery
    ) {}

    add(client: Client | Client[]): void {
        this.clientStore.add(client);
    }

    update(id: string, client: Partial<Client>): void {
        this.clientStore.update(id, client);
    }

    remove(id?: string): void {
        this.clientStore.remove(id);
    }

    setActive(id: string): void {
        this.clientStore.setActive(id);
    }

    setCurrentClient(client: Client): void {
        this.clientStore.update({
            currentClient: client,
        });
    }

    getClientsByName(name: string): Observable<Client[]> {
        this.clientStore.update({
            userSearch: name,
        });

        const someSubstringValid = name
            .trim()
            .split(' ')
            .some((str) => str.length >= 3);

        if (!someSubstringValid && name !== '*') {
            this.remove();
            return EMPTY;
        }

        return this.searchClient(name, 0).pipe(
            map((response) => JSON.parse(response)),
            tap(({ data }) => this.updatePagination(name, data)),
            map(({ data }) => {
                /**
                 * If there are client result returns 2 lists:
                 * - __list_0: clients list
                 * - __list_1: pagination list
                 *
                 * If there are not results returns 1 list:
                 * - __list_0: pagination list
                 */
                let clients = data.__list_0;
                this.remove();

                if (!data.__list_0[0].page) {
                    clients = clients.map((client) => createClient(client));

                    if (clients.length > 0) {
                        this.clientStore.upsertMany(clients);
                    } else {
                        this.remove();
                    }

                    return clients;
                }

                return [];
            })
        );
    }

    getClientById(id: string): Observable<Client> {
        const form = new AesRequestModel('SEC_CLIENT_OPEN_FORM', [
            { Param: 'ClientID', Value: id, AESIndex: '1' },
        ]);

        return this.apiService.callApiObservable(form).pipe(
            map((response) => JSON.parse(response)),
            map(({ data }) => {
                this.setCurrentClient(this.buildClient(data));
                this.setActive(id);

                return this.clientQuery.getCurrentClient();
            })
        );
    }

    getClientHistory(clientId: string): Observable<{
        nextAppointments: ClientNextHistory[];
        previousAppointments: ClientPreviousHistory[];
    }> {
        const form = new AesRequestModel('SEC_CLIENT_HISTORY_FORM', [
            { Param: 'ClientID', Value: clientId, AESIndex: '1' },
            { Param: 'SetCurrentClient', Value: '0', AESIndex: '2' },
            { Param: 'Page', Value: '0', AESIndex: '3' },
        ]);

        return this.apiService.callApiObservable(form).pipe(
            map((response) => JSON.parse(response)),
            map(({ data }) => {
                const nextAppointments = data.__list_0.map(
                    createClientNextHistory
                );
                const previousAppointments = data.__list_1.map(
                    createClientPreviousHistory
                );

                return { nextAppointments, previousAppointments };
            })
        );
    }

    getClinicHistory(clientId: string): Observable<ClientClinicHist> {
        const formName = this.settingsQuery.getValue().visualDoctor ? 'SEC_CLINIC_HISTORY_LIST_LOAD_FORM' : 'SEC_PRESCRIPTION_LIST_LOAD_FORM';
        const form = new AesRequestModel(formName, [
            { Param: 'ClientID', Value: clientId, AESIndex: '1' },
            { Param: 'LaserGID', Value: '', AESIndex: '2' },
        ]);

        return this.apiService.callApiObservable(form).pipe(
            map((response) => JSON.parse(response)),
            map(({ data }) => {
                const { aesUrl, sessionId } = this.sessionQuery.getValue();
                return  createClientClinicHist(data?.clinicHistory ? JSON.parse(data.clinicHistory) : [], aesUrl, sessionId);
            })
        );
    }

    saveClinicAnnotation(
        clinicAnnotation: ClientClinicHistClinicAnnotation,
        date: string,
        sessionId: string,
        clientId: string,
        newAnnotation: boolean
    ) {
        const form = new AesRequestModel(newAnnotation ?  'SEC_PRESCRIPTION_NEW_FORM' : 'SEC_PRESCRIPTION_EDIT_FORM', [
            { Param: 'PrescriptionID', Value: clinicAnnotation ? clinicAnnotation.id : '0', AESIndex: '1' },
            { Param: 'ClientID', Value: clientId, AESIndex: '2' },
            { Param: 'New/Update CModel', Value: newAnnotation ? '-1' : '0', AESIndex: '3' },
            { Param: 'Session', Value: sessionId, AESIndex: '4' },
            { Param: 'Text', Value: clinicAnnotation.text, AESIndex: '5' },
        ]);

        return this.apiService
            .callApiObservable(form)
            .pipe(
                map((response) => JSON.parse(response)),
                map(({ data }) =>
                    //Todo devolver algo?
                     true
                )
            )
            .toPromise();
    }

    getClientsPagination(page: number): Observable<Client[]> {
        const name = this.clientStore.getValue().name;

        return this.searchClient(name, page).pipe(
            map((response) => JSON.parse(response)),
            tap(({ data }) => this.updatePagination(name, data)),
            map(({ data }) => {
                /**
                 * If there are client result returns 2 lists:
                 * - __list_0: clients list
                 * - __list_1: pagination list
                 *
                 * If there are not results returns 1 list:
                 * - __list_0: pagination list
                 */
                let clients = data.__list_0;

                if (!data.__list_0[0].page) {
                    clients = clients.map((client) => createClient(client));

                    if (clients.length > 0) {
                        this.clientStore.upsertMany(clients);
                    }

                    return clients;
                }

                return [];
            })
        );
    }

    getMergeClients(
        clientId: string
    ): Observable<{ clients: Client[]; duplicityType: string }> {
        const form = new AesRequestModel('SEC_CLIENT_APP_MERGE_SEARCH_FORM', [
            { Param: 'clientID', Value: clientId, AESIndex: '1' },
        ]);

        return this.apiService.callApiObservable(form).pipe(
            map((response) => JSON.parse(response)),
            map(({ data }) => {
                const clients: Client[] = [];

                if (data.__list_0) {
                    data.__list_0.forEach((item) => {
                        clients.push({
                            email: item.clientemail,
                            id: item.clientid,
                            name: item.clientname,
                            phone: item.clientphone,
                            sex: item.clientsex,
                            surname1: item.clientsurname1,
                            surname2: item.clientsurname2,
                        } as Client);
                    });
                }

                return { clients, duplicityType: data.DuplicityType };
            })
        );
    }

    createMergeClient(client: Client): Observable<string> {
        const form = new AesRequestModel('SEC_CLIENT_APP_MERGE_NEW_FORM', [
            { Param: 'clientID', Value: client.id, AESIndex: '1' },
        ]);

        return this.apiService
            .callApiObservable(form)
            .pipe(map((response) => JSON.parse(response)));
    }

    deleteMergeClient(client: Client): Observable<string> {
        const form = new AesRequestModel('SEC_CLIENT_DELETE_FORM', [
            { Param: 'clientID', Value: client.id, AESIndex: '1' },
        ]);

        return this.apiService
            .callApiObservable(form)
            .pipe(map((response) => JSON.parse(response)));
    }

    createClient(
        client: Client,
        checkDup: boolean
    ): Observable<{
        inf: string;
        clientId: string;
        duplicityType: string;
        duplicateClients: Client[];
    }> {
        return this.saveClient(client, true, checkDup);
    }

    editClient(
        client: Client,
        checkDup: boolean
    ): Observable<{
        inf: string;
        clientId: string;
        duplicityType: string;
        duplicateClients: Client[];
    }> {
        // Merge new client properties with old ones to complete the model
        const currentClient = this.clientQuery.getCurrentClient();
        const newClient = {
            ...currentClient,
            ...client,
        };
        return this.saveClient(newClient, false, checkDup);
    }

    mergeClient(
        currentClient: Client,
        mergeClient: Client
    ): Observable<string> {
        const form = new AesRequestModel('SEC_CLIENT_MERGE_FORM', [
            { Param: 'clientID2Keep', Value: mergeClient.id, AESIndex: '1' },
            { Param: 'clientID2Merge', Value: currentClient.id, AESIndex: '2' },
            { Param: 'mergeConfirmed', Value: '-1', AESIndex: '3' },
        ]);

        return this.apiService.callApiObservable(form).pipe(
            map((response) => JSON.parse(response)),
            map(({ data }) => data.ClientID)
        );
    }

    saveForm(document: ClientDocument, clientId: string, doctorMode: DoctorMode = DoctorMode.ANAMNESIS): Observable<any> {
        const data: AesRequestFieldModel[] = [
            {
                Param: 'FormId|ClientId|Doctor',
                Value: `${document.id}|${clientId}|${
                    document.doctorId ? document.doctorId : '0'
                }`,
                AESIndex: '1',
            },
            {
                Param: 'HairClassID',
                Value: document.hairClassId,
                AESIndex: '2',
            },
            {
                Param: 'PhotoClassID',
                Value: document.photoClassId,
                AESIndex: '3',
            },
            { Param: 'Laser', Value: document.laser, AESIndex: '4' },
            {
                Param: 'LOPD1',
                Value: document.lopd1 ? '-1' : '0',
                AESIndex: '5',
            },
            {
                Param: 'LOPD2',
                Value: document.lopd2 ? '-1' : '0',
                AESIndex: '6',
            },
            {
                Param: 'LOPDDate',
                Value: document.lopdDate.format(FLOW_DATE_FORMAT),
                AESIndex: '7',
            },
            { Param: 'Diagnostic', Value: document.diagnostic, AESIndex: '8' },
        ];

        document.fields.forEach((field) => {
            if (field.type === DocumentEditFieldType.GROUP) {
                field.checkboxes.forEach((checkbox) => {
                    data.push({
                        Param: '',
                        Value: checkbox.value,
                        AESIndex: checkbox.name,
                    });
                });
            } else {
                data.push({
                    Param: '',
                    Value: field.value,
                    AESIndex: field.name,
                });
            }
        });

        const doctorModeSubstring = doctorMode === DoctorMode.DOCTOR ? 'DOCTOR' : 'ANAMNESIS';
        const form = new AesRequestModel('SEC_'+doctorModeSubstring+'_SAVE_FORM', data);

        return this.apiService.callApiObservable(form);
    }

    saveEditableForm(jsonObj: any): Observable<any> {
        const data: AesRequestFieldModel[] = [
            {
                Param: 'JSON',
                Value: JSON.stringify(jsonObj),
                AESIndex: '1',
            }
        ];

        const form = new AesRequestModel('SEC_EDITABLE_DOCTOR_SAVE_FORM', data);

        return this.apiService.callApiObservable(form);
    }

    private buildClient(data: any): Client {
        const settings = this.settingsQuery.getValue();
        const referrer = this.clinicQuery
            .getActive()
            .referrers.find((i) => i.id === data.ClientRefererID)?.description;

        // Save client custom fields
        const customFieldLabels: string[] =
            settings.clientCustomFieldsLabels.split(',');
        const customFieldEnabled: string[] =
            settings.clientCustomFieldsEnable.split(',');
        const customFields: CustomField[] = [];
        for (
            let i = 0, index = 1;
            i < DEF_NUMBER_OF_CLIENT_CUSTOM_FIELDS;
            i++, index++
        ) {
            const customField = data[`ClientCustom${index}`];

            // Custom field not exists
            if (!customField) {
                continue;
            }

            const value = customField.value;
            let options: string[] = [];

            if (customField.customtype.includes('select')) {
                options = customField.customtype.split(';');
                options.shift();
            }

            const type = customField.customtype.split(
                ';'
            )[0] as CustomFieldTypes;
            customFields.push({
                enabled: customFieldEnabled[i] !== '0',
                label: customFieldLabels[i],
                id: index,
                options,
                readonly: customFieldEnabled[i] === '-2',
                value:
                    type === CustomFieldTypes.CHECKBOX && value === ''
                        ? '0'
                        : value, // Si el campo custom llega vacío de back y además es de tipo checkbox, hay que manejarlo como si fuera false (0)
                type,
            });
        }

        return {
            address: data.ClientAddress,
            appDisabled: '0',
            birthDate:
                data.ClientBirthDate !== ''
                    ? moment(data.ClientBirthDate, FLOW_DATE_FORMAT)
                    : null,
            burn: data.ClientBurn,
            cardNumber: data.ClientCardNumber,
            cityId: data.ClientCityID,
            cityName: data.ClientCityName,
            colony: data.ClientColony,
            country: this.sessionQuery.getCountryById(data.ClientCountryID),
            customFields,
            date: data.ClientDate,
            deferred: data.ClientDeferred !== '0',
            delegation: data.ClientDelegation,
            documentType: data.ClientDocumentType,
            email: data.ClientEMail,
            enableGc: data.ClientEnableGeneralConditions,
            enableRGPD: data.ClientEnableRGPD,
            extra: data.ClientExtra,
            fwa: data.ClientFWA,
            fwaLogin: data.ClientFWALogin,
            fwaPassword: data.ClientFWAPassword,
            history: data.ClientHistory,
            id: data.ClientID,
            iessuf: data.ClientIESSUF,
            job: data.ClientJob,
            legalRepresentative: {
                address: data.LegalRepresentativeAddress,
                documentType: data.LegalRepresentativeDocumentType,
                email: data.LegalRepresentativeEMail,
                name: data.LegalRepresentativeName,
                nif: data.LegalRepresentativeNIF,
                parenthood: data.LegalRepresentativeRelationship,
                phone: data.LegalRepresentativePhone1,
                surname1: data.LegalRepresentativeSurnames,
            },
            localIe: data.ClientLocalIE,
            longNotes: data.ClientLNotes,
            mobileClassId: data.ClientMobileClassID,
            name: data.ClientName,
            nif: data.ClientNIF,
            notes: data.ClientNotes,
            number: data.ClientNumber,
            phone: data.ClientPhone1,
            phone2: data.ClientPhone2,
            phone3: data.ClientPhone3,
            phonePrefix: data.ClientPhonePrefix1,
            phonePrefix2: data.ClientPhonePrefix2,
            phonePrefix3: data.ClientPhonePrefix3,
            postCode: data.ClientPostCode,
            profileImage: data.ClientThumb?.replace(/(\r\n|\n|\r)/gm, ''), // Backend returns bad base64
            provinceId: data.ClientProvinceID,
            referrer,
            referrerId: data.ClientRefererID,
            rg: data.ClientRG,
            sex: data.ClientSex,
            strNum: data.ClientStrNum,
            surname1: data.ClientSurname1,
            surname2: data.ClientSurname2,
            temporal: false,
            vouchers: data.ClientVouchers,
            vip: data.ClientVIP,
        } as Client;
    }

    private saveClient(
        client: Client,
        isNewClient: boolean,
        checkDup: boolean
    ): Observable<{
        inf: string;
        clientId: string;
        duplicityType: string;
        duplicateClients: Client[];
    }> {
        const form = new AesRequestModel(
            isNewClient ? 'SEC_CLIENT_NEW_FORM' : 'SEC_CLIENT_EDIT_FORM',
            [
                { Param: 'ClientName', Value: client.name, AESIndex: '1' },
                {
                    Param: 'ClientBirthDate',
                    Value:
                        client.birthDate && client.birthDate.isValid()
                            ? client.birthDate.format(FLOW_DATE_FORMAT)
                            : '',
                    AESIndex: '2',
                },
                { Param: 'ClientNIF', Value: client.nif, AESIndex: '5' },
                { Param: 'ClientPhone1', Value: client.phone, AESIndex: '6' },
                { Param: 'ClientPhone2', Value: client.phone2, AESIndex: '7' },
                {
                    Param: 'ClientAddress',
                    Value: client.address,
                    AESIndex: '8',
                },
                { Param: 'ClientCity', Value: client.cityName, AESIndex: '9' },
                {
                    Param: 'ClientPostCode',
                    Value: client.postCode,
                    AESIndex: '10',
                },
                {
                    Param: 'ClientProvinceID',
                    Value: client.provinceId,
                    AESIndex: '11',
                },
                {
                    Param: 'ClientCountryID',
                    Value: client.country.id,
                    AESIndex: '12',
                },
                { Param: 'ClientEmail', Value: client.email, AESIndex: '13' },
                { Param: 'ClientNotes', Value: client.notes, AESIndex: '14' },
                { Param: 'ClientID', Value: client.id, AESIndex: '15' },
                {
                    Param: 'ClientEnableRGPD',
                    Value: client.enableRGPD,
                    AESIndex: '16',
                },
                {
                    Param: 'ClientLNotes',
                    Value: client.longNotes,
                    AESIndex: '18',
                },
                { Param: 'ClientBurn', Value: client.burn, AESIndex: '19' },
                {
                    Param: 'ClientSurname1',
                    Value: client.surname1,
                    AESIndex: '20',
                },
                {
                    Param: 'ClientSurname2',
                    Value: client.surname2,
                    AESIndex: '21',
                },
                { Param: 'ClientPhone3', Value: client.phone3, AESIndex: '22' },
                { Param: 'ClientStrNum', Value: client.strNum, AESIndex: '23' },
                { Param: 'ClientExtra', Value: client.extra, AESIndex: '24' },
                {
                    Param: 'CheckDup',
                    Value: checkDup ? '-1' : '0',
                    AESIndex: '28',
                },
                { Param: 'ClientColony', Value: client.colony, AESIndex: '29' },
                {
                    Param: 'ClientDelegation',
                    Value: client.delegation,
                    AESIndex: '30',
                },
                {
                    Param: 'ClientRefererID',
                    Value: client.referrerId,
                    AESIndex: '31',
                },
                { Param: 'ClientSex', Value: client.sex, AESIndex: '33' },
                {
                    Param: 'ClientMobileClassID',
                    Value: client.mobileClassId,
                    AESIndex: '34',
                },
                {
                    Param: 'ClientCardNumber',
                    Value: client.cardNumber,
                    AESIndex: '35',
                },
                {
                    Param: 'ClientPhonePrefix1',
                    Value: client.phonePrefix,
                    AESIndex: '36',
                },
                {
                    Param: 'ClientPhonePrefix2',
                    Value: client.phonePrefix2,
                    AESIndex: '37',
                },
                {
                    Param: 'ClientPhonePrefix3',
                    Value: client.phonePrefix3,
                    AESIndex: '38',
                },
                { Param: 'ClientRG', Value: client.rg, AESIndex: '39' },
                { Param: 'ClientVIP', Value: client.vip, AESIndex: '40' },
            ]
        );

        for (
            let i = 0, index = 1;
            i < client.customFields.length;
            i++, index++
        ) {
            let value = '';
            if (client.customFields[i]) {
                value = client.customFields[i].value;
            }

            form.Data.push({
                Param: `ClientCustom${index}`,
                Value: value,
                AESIndex: (40 + client.customFields[i].id).toString(),
            });
        }

        form.Data = form.Data.concat([
            { Param: 'ClientJob', Value: client.job, AESIndex: '51' },
            { Param: 'ClientCityID', Value: client.cityId, AESIndex: '52' },
            { Param: 'ClientLocalIE', Value: client.localIe, AESIndex: '53' },
            { Param: 'ClientIESSUF', Value: client.iessuf, AESIndex: '54' },
            { Param: 'ClientFWALogin', Value: client.fwaLogin, AESIndex: '55' },
            {
                Param: 'ClientFWAPassword',
                Value: client.fwaPassword,
                AESIndex: '56',
            },
            {
                Param: 'ClientAppDisabled',
                Value: client.appDisabled,
                AESIndex: '57',
            },
            { Param: 'ClientEnableGC', Value: client.enableGc, AESIndex: '58' },
            {
                Param: 'ClientDocumentType',
                Value: client.documentType,
                AESIndex: '59',
            },
            {
                Param: 'ClientTmp',
                Value: client.temporal ? '-1' : '0',
                AESIndex: '60',
            },
            {
                Param: 'ClientRepParenthood',
                Value: client.legalRepresentative.parenthood,
                AESIndex: '61',
            },
            {
                Param: 'ClientRepName',
                Value: client.legalRepresentative.name,
                AESIndex: '62',
            },
            {
                Param: 'ClientRepSurname',
                Value: client.legalRepresentative.surname1,
                AESIndex: '63',
            },
            {
                Param: 'ClientRepAddress',
                Value: client.legalRepresentative.address,
                AESIndex: '64',
            },
            {
                Param: 'ClientRepPhone',
                Value: client.legalRepresentative.phone,
                AESIndex: '65',
            },
            {
                Param: 'ClientRepIDClass',
                Value: client.legalRepresentative.documentType,
                AESIndex: '66',
            },
            {
                Param: 'ClientRepNIF',
                Value: client.legalRepresentative.nif,
                AESIndex: '67',
            },
            {
                Param: 'ClientRepEmail',
                Value: client.legalRepresentative.email,
                AESIndex: '68',
            },
        ]);

        return this.apiService.callApiObservable(form).pipe(
            map((response) => JSON.parse(response)),
            map(({ inf, data }) => ({
                inf,
                clientId: data.ClientID,
                duplicityType: data.DuplicityType,
                duplicateClients: data.__list_0?.map(createDuplicateClient),
            }))
        );
    }

    private searchClient(name: string, page: number): Observable<string> {
        const form = new AesRequestModel(
            'SEC_CLIENT_SEARCH_FORM',
            [
                { Param: 'ClientName', Value: name, AESIndex: '1' },
                { Param: 'NumPage', Value: page, AESIndex: '6' },
            ],
            38
        );

        return this.apiService.callApiObservable(form);
    }

    private updatePagination(
        name: string,
        { __list_1 }: { __list_1: { page: string; numpages: string }[] }
    ): void {
        let currentPage;
        let pages;

        if (__list_1) {
            currentPage = parseInt(__list_1[0].page, 10);
            pages = parseInt(__list_1[0].numpages, 10);
        }

        this.clientStore.update({
            currentPage,
            pages,
            name,
        });
    }
}
